/**
 * Hook to fetch AI models from models.dev API
 */

import { useEffect, useState } from "react";
import type { ModelInfo, ProviderInfo } from "@/types/models.ts";
import { loadTokensForProvider } from "@/lib/auth/tokens.ts";

const MODELS_API_URL = "https://models.dev/api.json";
const CACHE_KEY = "models_dev_cache";
const CACHE_DURATION = 1000 * 60 * 60 * 24; // 24 hours
const OPENROUTER_MODELS_URL = "https://openrouter.ai/api/v1/models";
const OPENROUTER_CACHE_KEY = "openrouter_models_cache";

interface CachedData {
  data: ModelsApiResponse;
  timestamp: number;
}

type ModelsApiResponse = Record<string, ProviderInfo>;

interface OpenRouterModelsResponse {
  data: Array<{
    id: string;
    name?: string;
    description?: string;
    context_length?: number;
    pricing?: {
      prompt?: number;
      completion?: number;
    };
  }>;
}

/**
 * Provider mapping from our internal names to models.dev provider keys
 */
const PROVIDER_MAP: Record<string, string> = {
  copilot: "github-copilot",
  antigravity: "google-vertex-anthropic", // Google Antigravity might use Vertex
  gemini: "google",
  groq: "groq",
  perplexity: "perplexity",
};

export interface UseModelsReturn {
  models: ModelInfo[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch models for a specific provider
 */
export function useModels(provider?: string): UseModelsReturn {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModels = async () => {
    if (!provider) {
      setModels([]);
      return;
    }

    // Default provider uses server-side model selection — no model list to fetch
    if (provider === "default") {
      setModels([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (provider === "openrouter") {
        const tokens = loadTokensForProvider("openrouter");
        if (!tokens?.accessToken) {
          throw new Error("OpenRouter API key is not set");
        }

        const cached = localStorage.getItem(OPENROUTER_CACHE_KEY);
        let cachedModels: ModelInfo[] | null = null;

        if (cached) {
          const cachedData: CachedData & { data: ModelInfo[] } = JSON.parse(cached);
          const age = Date.now() - cachedData.timestamp;
          if (age < CACHE_DURATION) {
            cachedModels = cachedData.data;
          }
        }

        if (!cachedModels) {
          const response = await fetch(OPENROUTER_MODELS_URL, {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
              "HTTP-Referer": globalThis.location.origin,
              "X-Title": "Plot-Cat",
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch OpenRouter models: ${response.statusText}`);
          }

          const data = await response.json() as OpenRouterModelsResponse;
          cachedModels = (data.data || []).map((model) => ({
            id: model.id,
            name: model.name || model.id,
            knowledge: model.description,
            cost: model.pricing
              ? {
                input: model.pricing.prompt ?? 0,
                output: model.pricing.completion ?? 0,
              }
              : undefined,
          }));

          localStorage.setItem(
            OPENROUTER_CACHE_KEY,
            JSON.stringify({
              data: cachedModels,
              timestamp: Date.now(),
            }),
          );
        }

        setModels(cachedModels);
        return;
      }

      // Check cache first
      const cached = localStorage.getItem(CACHE_KEY);
      let apiData: ModelsApiResponse | null = null;

      if (cached) {
        const cachedData: CachedData = JSON.parse(cached);
        const age = Date.now() - cachedData.timestamp;

        if (age < CACHE_DURATION) {
          apiData = cachedData.data;
          console.log("Using cached models data");
        }
      }

      // Fetch if no cache or expired
      if (!apiData) {
        console.log("Fetching models from API...");
        const response = await fetch(MODELS_API_URL);
        if (!response.ok) {
          throw new Error(`Failed to fetch models: ${response.statusText}`);
        }
        apiData = await response.json() as ModelsApiResponse;

        // Cache the data
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            data: apiData,
            timestamp: Date.now(),
          }),
        );
      }

      // Get provider key
      const providerKey = PROVIDER_MAP[provider] || provider;
      const providerInfo: ProviderInfo | undefined = apiData[providerKey];

      if (!providerInfo || !providerInfo.models) {
        throw new Error(`Provider "${providerKey}" not found in API`);
      }

      // Convert models object to array
      const modelsList = Object.values(providerInfo.models);
      setModels(modelsList);
    } catch (err) {
      console.error("Error fetching models:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch models");
      setModels([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [provider]);

  return {
    models,
    isLoading,
    error,
    refetch: fetchModels,
  };
}

/**
 * Fetch all models from all providers (for the StoryEditor dropdown)
 */
export function useAllModels(): UseModelsReturn {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllModels = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check cache first
      const cached = localStorage.getItem(CACHE_KEY);
      let apiData: ModelsApiResponse | null = null;

      if (cached) {
        const cachedData: CachedData = JSON.parse(cached);
        const age = Date.now() - cachedData.timestamp;

        if (age < CACHE_DURATION) {
          apiData = cachedData.data;
        }
      }

      // Fetch if no cache or expired
      if (!apiData) {
        const response = await fetch(MODELS_API_URL);
        if (!response.ok) {
          throw new Error(`Failed to fetch models: ${response.statusText}`);
        }
        apiData = await response.json() as ModelsApiResponse;

        // Cache the data
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            data: apiData,
            timestamp: Date.now(),
          }),
        );
      }

      // Collect models from all configured providers
      const allModels: ModelInfo[] = [];

      for (const [_internalName, providerKey] of Object.entries(PROVIDER_MAP)) {
        const providerInfo: ProviderInfo | undefined = apiData[providerKey];
        if (providerInfo?.models) {
          allModels.push(...Object.values(providerInfo.models));
        }
      }

      // Remove duplicates by id
      const uniqueModels = Array.from(
        new Map(allModels.map((m) => [m.id, m])).values(),
      );

      // Sort by name
      uniqueModels.sort((a, b) => a.name.localeCompare(b.name));

      setModels(uniqueModels);
    } catch (err) {
      console.error("Error fetching models:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch models");
      setModels([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllModels();
  }, []);

  return {
    models,
    isLoading,
    error,
    refetch: fetchAllModels,
  };
}
